import {
  Autocomplete,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import ByInterfaceTable from "./ByInterfaceTable";
import ByInterfaceTableSkeleton from "./ByInterfaceTableSkeleton";
import SummaryStripSkeleton from "./SummaryStripSkeleton";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectUserToken } from "../../../../features/userSlice";
import { familiesEndPointLogic } from "../../../../logic/DataCollection/Families/FamiliesEndPointLogic";
import { useWebsiteSharedUid } from "../../../../customHooks/useWebsiteSharedUid";

const tableHeadings = {
  interface: "Interface",
  brands: "Brands",
  fetcher: "Fetcher",
  unifier: "Unifier",
  advisor: "Advisor",
  maps_config: "Maps/Config",
};

const legend = [
  { label: "Fetcher", color: "#607D8B" },
  { label: "Unifier", color: "#E91E63" },
  { label: "Advisor", color: "#2196F3" },
];

const ByInterface = () => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  // Families data spans all interfaces/brands, not scoped to one website —
  // the EP treats website_shared_uid as a required dummy input here, so we
  // pass the first website_shared_uid the user can access from /me.
  const website_shared_uid = useWebsiteSharedUid();
  const {
    interfaceCoverage,
    interfaceCoverageTotals,
    isInterfaceCoverageLoading,
    brandStepCoverage,
  } = useSelector((state: any) => state.families);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get("interfaceSearch") ?? "";
  const setSearchTerm = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("interfaceSearch", value);
    } else {
      next.delete("interfaceSearch");
    }
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    familiesEndPointLogic.getInterfaceCoverage(
      oauthTokenHeader,
      website_shared_uid,
      dispatch,
    );
    if (!brandStepCoverage.length) {
      familiesEndPointLogic.getBrandStepCoverage(
        oauthTokenHeader,
        website_shared_uid,
        dispatch,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cards = [
    {
      title: "FAMILIES",
      description: "A collection of brands in a single group.",
      count: interfaceCoverageTotals?.families ?? "N/A",
    },
    {
      title: "INTERFACES",
      description: "Select one to manage its families.",
      count: interfaceCoverageTotals?.interfaces ?? "N/A",
    },
    {
      title: "BRANDS MISSING A STEP",
      description: "The number of brands associated.",
      count:
        interfaceCoverageTotals?.missing_a_step != null &&
        interfaceCoverageTotals?.brands_total != null
          ? `${interfaceCoverageTotals.missing_a_step}/${interfaceCoverageTotals.brands_total}`
          : "N/A",
    },
  ];

  const searchOptions = useMemo(
    () =>
      interfaceCoverage.map((row: any) => ({
        label: row.interface_name,
      })),
    [interfaceCoverage],
  );

  const rows = useMemo(() => {
    const filtered = searchTerm
      ? interfaceCoverage.filter((row: any) =>
          row.interface_name?.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : interfaceCoverage;

    return filtered.map((row: any) => ({
      backend_interface_id: row.backend_interface_id,
      interface: row.interface_name,
      brands: row.brands_total,
      fetcher: row.fetcher,
      unifier: row.unifier,
      advisor: row.advisor,
      maps_config: row.maps_total,
    }));
  }, [interfaceCoverage, searchTerm]);

  return (
    <>
      <Box sx={{ padding: "10px 16px 20px" }}>
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            width: "100%",
          }}
        >
          {isInterfaceCoverageLoading ? (
            <SummaryStripSkeleton />
          ) : (
            cards.map((card, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  border: "1px solid #0000001F",
                  borderRadius: "8px",
                  padding: "8px",
                  height: "66px",
                  boxSizing: "border-box",
                }}
              >
                {/* Title + Count */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      pb={0.5}
                      sx={{
                        fontSize: "16px",
                        color:'#00000099'
                      }}
                    >
                      {card.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color:'#00000099'
                      }}
                    >
                      {card.description}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "24px",
                      fontWeight: 700,
                      color: "#292929",
                      lineHeight: 1,
                    }}
                  >
                    {card.count}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>

      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ padding: "0px 16px 16px" }}
      >
        <Grid size={3} data-tracker="981">
          <Autocomplete
            sx={{ width: "100%" }}
            options={searchOptions}
            freeSolo
            inputValue={searchTerm}
            onInputChange={(_, value) => setSearchTerm(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search Interface Name"
                label="Search Interface Name"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
              />
            )}
            size="small"
          />
        </Grid>
        <Grid size={3}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              justifyContent: "flex-end",
            }}
          >
            {legend.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: item.color,
                    borderRadius: "2px",
                  }}
                />

                <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>

      {isInterfaceCoverageLoading ? (
        <ByInterfaceTableSkeleton />
      ) : (
        <ByInterfaceTable
          tableHeadings={tableHeadings}
          rows={rows}
          brandStepCoverage={brandStepCoverage}
        />
      )}
    </>
  );
};

export default ByInterface;
